import { useGuardedDefaultValues } from "@msuite/ui/hooks";
import { resolver } from "@msuite/ui/libs/form";
import { useTranslation } from "@msuite/ui/localization";
import { Modal, type ModalProps } from "@msuite/ui/modal";
import { responseHandler } from "@msuite/ui/utils";
import { type TransferCarrierFormSchema, transferCarrierFormSchema } from "@msuite/utils/schemas";
import { FormProvider, useForm } from "react-hook-form";
import { ModalLoadFailed } from "@/components/modals";
import { TransferCarrierForm } from "@/forms/transfer-carrier-form";
import { client } from "@/hono";

/** Props Interface */
interface EditTransferCarrierModalProps extends ModalProps {
	onComplete?: () => Promise<void>;
	transferCarrierId: string;
}

/**
 * Edit a transfer carrier.
 *
 * The number is editable because a device gets re-labelled — back from repair with a new plate, or
 * a merged yard renumbered. Clearing the field leaves the number alone rather than allocating a
 * new one, which is the only reading that makes sense for something painted on the side of a
 * trolley.
 */
export function EditTransferCarrierModal({
	onClose,
	isOpen,
	onComplete,
	transferCarrierId,
}: EditTransferCarrierModalProps) {
	/** Hooks */
	const { t } = useTranslation();
	const { defaultValues, loadFailed } = useGuardedDefaultValues(async () => {
		const response = await client.stock["transfer-carriers"][":transferCarrierId"].$get({
			param: { transferCarrierId },
		});
		const data = await responseHandler(response);
		return transferCarrierFormSchema.parse(data.transferCarrier);
	});
	const methods = useForm({
		resolver: resolver(transferCarrierFormSchema),
		defaultValues,
	});

	/** Functions */
	async function handleOnSubmit(json: TransferCarrierFormSchema) {
		const response = await client.stock["transfer-carriers"][":transferCarrierId"].$patch({
			param: { transferCarrierId },
			json,
		});
		await responseHandler(response);
		await onComplete?.();
		onClose();
	}

	/** Render */
	return (
		<FormProvider {...methods}>
			<Modal
				title={t("stock.edit_transfer_carrier")}
				isOpen={isOpen}
				onClose={onClose}
				onSubmit={methods.handleSubmit(handleOnSubmit)}
				isLoading={methods.formState.isLoading && !loadFailed}
			>
				{loadFailed ? (
					<ModalLoadFailed onClose={onClose} />
				) : (
					<>
						<Modal.Body>
							<TransferCarrierForm />
						</Modal.Body>
						<Modal.FooterForm />
					</>
				)}
			</Modal>
		</FormProvider>
	);
}
